import { queryClient } from "../../lib/queryClient";
import { queryKeys } from "../../lib/queryKeys";
import {
  getConversationList,
  getConversationMessageList,
  getFacilitiesList,
  getHtmlUrl,
  getPdfUrl,
  getProviderDashboardEndFilter,
  getProviderDashboardStartFilter,
  getSpecialtiesList,
} from "../../services";

const asQueryParams = (params: object) => params as Record<string, unknown>;

export const fetchConversationListQuery = (params: object) =>
  queryClient.fetchQuery({
    queryKey: queryKeys.conversationList(asQueryParams(params)),
    queryFn: () => getConversationList({ params }),
  });

export const fetchConversationMessagesQuery = (params: object) =>
  queryClient.fetchQuery({
    queryKey: queryKeys.conversationMessages(asQueryParams(params)),
    queryFn: () => getConversationMessageList({ params }),
    staleTime: 0,
  });

export const fetchPdfUrlQuery = (queryId: string) =>
  queryClient.fetchQuery({
    queryKey: queryKeys.pdfUrl(queryId),
    queryFn: () => getPdfUrl({ query_id: queryId }),
    retry: false,
  });

export interface PdfUrlData {
  blobData: Blob | string;
  filename?: string;
  contentType?: string;
  size?: number;
}

/** Fetches query PDF with retries — backend may need time after submit to generate it. */
export const fetchPdfUrlWithRetry = async (
  queryId: string,
  options?: { attempts?: number; delayMs?: number }
): Promise<PdfUrlData | null> => {
  const { attempts = 4, delayMs = 1500 } = options ?? {};

  await queryClient.removeQueries({ queryKey: queryKeys.pdfUrl(queryId) });

  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      const response = await getPdfUrl({ query_id: queryId });
      const data = response?.data as PdfUrlData | undefined;
      if (data?.blobData) {
        return data;
      }
    } catch (error) {
      console.warn(
        `PDF fetch attempt ${attempt}/${attempts} failed for ${queryId}:`,
        error
      );
    }

    if (attempt < attempts) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  return null;
};

export const fetchHtmlUrlQuery = (queryId: string) =>
  queryClient.fetchQuery({
    queryKey: queryKeys.htmlUrl(queryId),
    queryFn: () => getHtmlUrl({ query_id: queryId }),
  });

export const fetchFacilitiesListQuery = (params: object) => {
  const queryParams = asQueryParams(params);
  return queryClient.fetchQuery({
    queryKey: queryKeys.facilitiesList(queryParams),
    queryFn: () => getFacilitiesList(queryParams),
  });
};

export const fetchSpecialtiesListQuery = (params: object) => {
  const queryParams = asQueryParams(params);
  return queryClient.fetchQuery({
    queryKey: queryKeys.specialtiesList(queryParams),
    queryFn: () => getSpecialtiesList(queryParams),
  });
};

export const fetchDashboardStartFilterQuery = (params: object) => {
  const queryParams = asQueryParams(params);
  return queryClient.fetchQuery({
    queryKey: queryKeys.dashboardStartFilter(queryParams),
    queryFn: () => getProviderDashboardStartFilter(queryParams),
  });
};

export const fetchDashboardEndFilterQuery = (params: object) => {
  const queryParams = asQueryParams(params);
  return queryClient.fetchQuery({
    queryKey: queryKeys.dashboardEndFilter(queryParams),
    queryFn: () => getProviderDashboardEndFilter(queryParams),
  });
};
